import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import AuthenticatedNavbar from '../headers/AuthenticatedNavbar';
import { getApiBaseUrl } from '../../config/api';
import { isAdmin } from '../../utils/userRole';
import './AssistantCalibrate.css';

const API_URL = getApiBaseUrl();

const EMPTY_ITEM = {
  id: 0,
  title: '',
  question: '',
  answer: '',
  keywords: '',
  category: 'general',
  sort_order: 0,
  is_active: 1,
};

const LLM_MODELS = {
  openai: [
    { value: 'gpt-5.6-luna', label: 'GPT-5.6 Luna — fast, lowest cost' },
    { value: 'gpt-5.6-terra', label: 'GPT-5.6 Terra — balanced' },
    { value: 'gpt-5.6-sol', label: 'GPT-5.6 Sol — most capable' },
    { value: 'gpt-4o-mini', label: 'GPT-4o mini' },
    { value: 'gpt-4o', label: 'GPT-4o' },
    { value: 'gpt-4.1-mini', label: 'GPT-4.1 mini' },
    { value: 'gpt-4.1', label: 'GPT-4.1' },
  ],
  groq: [
    { value: 'llama-3.1-8b-instant', label: 'Llama 3.1 8B Instant' },
    { value: 'llama-3.3-70b-versatile', label: 'Llama 3.3 70B' },
  ],
  gemini: [
    { value: 'gemini-2.0-flash', label: 'Gemini 2.0 Flash' },
    { value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash' },
    { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro' },
  ],
};

const DEFAULT_LLM_MODEL = {
  openai: 'gpt-5.6-luna',
  groq: 'llama-3.1-8b-instant',
  gemini: 'gemini-2.0-flash',
};

export default function AssistantCalibrate({ user, onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [settings, setSettings] = useState({
    name: 'COOL Assistant',
    welcome_message: '',
    system_prompt: '',
    avatar_url: '',
    enabled: 1,
    suggested_prompts: [],
    llm_provider: 'none',
    llm_model: '',
    llm_api_key_masked: '',
    llm_api_key_set: false,
  });
  const [apiKeyDraft, setApiKeyDraft] = useState('');
  const [suggestedText, setSuggestedText] = useState('');
  const [knowledge, setKnowledge] = useState([]);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState('');
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const avatarInputRef = useRef(null);
  const knowledgeInputRef = useRef(null);

  const load = useCallback(async () => {
    if (!API_URL || !user?.id) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(
        `${API_URL}/assistant_admin.php?user_id=${encodeURIComponent(user.id)}&action=get`
      );
      const data = await res.json();
      if (!data.success) {
        throw new Error(data.message || 'Failed to load assistant');
      }
      setSettings((prev) => ({ ...prev, ...data.settings }));
      const prompts = data.settings?.suggested_prompts || [];
      setSuggestedText(Array.isArray(prompts) ? prompts.join('\n') : '');
      setKnowledge(data.knowledge || []);
      setApiKeyDraft('');
    } catch (err) {
      setError(err.message || 'Failed to load assistant settings');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    load();
  }, [load]);

  if (!user) {
    return <Navigate to="/login" replace />;
  }
  if (!isAdmin(user)) {
    return <Navigate to="/home" replace />;
  }

  const adminPost = async (payload) => {
    const res = await fetch(`${API_URL}/assistant_admin.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...payload, user_id: user.id }),
    });
    return res.json();
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    setSaving(true);
    setNotice('');
    setError('');
    try {
      const data = await adminPost({
        action: 'save_settings',
        name: settings.name,
        welcome_message: settings.welcome_message,
        system_prompt: settings.system_prompt,
        avatar_url: settings.avatar_url,
        enabled: settings.enabled ? 1 : 0,
        llm_provider: settings.llm_provider,
        llm_model: settings.llm_model,
        llm_api_key: apiKeyDraft,
        suggested_prompts: suggestedText
          .split(/\r?\n/)
          .map((s) => s.trim())
          .filter(Boolean),
      });
      if (!data.success) throw new Error(data.message);
      setSettings((prev) => ({ ...prev, ...data.settings }));
      setApiKeyDraft('');
      setNotice('Assistant settings saved. Changes apply to the chat bubble immediately.');
    } catch (err) {
      setError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const saveKnowledge = async (e) => {
    e.preventDefault();
    if (!editing) return;
    setSaving(true);
    setNotice('');
    setError('');
    try {
      const data = await adminPost({
        action: 'save_knowledge',
        ...editing,
        is_active: editing.is_active ? 1 : 0,
      });
      if (!data.success) throw new Error(data.message);
      setKnowledge(data.knowledge || []);
      setEditing(null);
      setNotice('Knowledge item saved. The assistant will use it on the next question.');
    } catch (err) {
      setError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const deleteKnowledge = async (item) => {
    if (!window.confirm(`Delete “${item.title}”?`)) return;
    setSaving(true);
    setError('');
    try {
      const data = await adminPost({ action: 'delete_knowledge', id: item.id });
      if (!data.success) throw new Error(data.message);
      setKnowledge(data.knowledge || []);
      if (editing?.id === item.id) setEditing(null);
      setNotice('Knowledge item deleted.');
    } catch (err) {
      setError(err.message || 'Delete failed');
    } finally {
      setSaving(false);
    }
  };

  const restoreDefaults = async () => {
    if (
      !window.confirm(
        'Restore the default COOL knowledge and personality? Custom Q&A you typed will be replaced. Uploaded files and API keys are kept.'
      )
    ) {
      return;
    }
    setSaving(true);
    setError('');
    try {
      const data = await adminPost({ action: 'restore_defaults' });
      if (!data.success) throw new Error(data.message);
      setSettings((prev) => ({ ...prev, ...data.settings }));
      const prompts = data.settings?.suggested_prompts || [];
      setSuggestedText(Array.isArray(prompts) ? prompts.join('\n') : '');
      setKnowledge(data.knowledge || []);
      setEditing(null);
      setNotice('Default COOL knowledge restored.');
    } catch (err) {
      setError(err.message || 'Restore failed');
    } finally {
      setSaving(false);
    }
  };

  const uploadForm = async (kind, files) => {
    const form = new FormData();
    form.append('user_id', String(user.id));
    form.append('kind', kind);
    if (kind === 'avatar') {
      form.append('avatar', files[0]);
    } else {
      Array.from(files).forEach((file) => form.append('files[]', file));
    }
    const res = await fetch(`${API_URL}/assistant_upload.php`, {
      method: 'POST',
      body: form,
    });
    return res.json();
  };

  const uploadAvatar = async (file) => {
    if (!file) return;
    setUploading(true);
    setNotice('');
    setError('');
    try {
      const data = await uploadForm('avatar', [file]);
      if (!data.success) throw new Error(data.message);
      setSettings((prev) => ({ ...prev, ...data.settings, avatar_url: data.avatar_url }));
      setNotice('Avatar uploaded. Save personality if you also changed other fields.');
    } catch (err) {
      setError(err.message || 'Avatar upload failed');
    } finally {
      setUploading(false);
    }
  };

  const uploadKnowledgeFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    setNotice('');
    setError('');
    try {
      const data = await uploadForm('knowledge', files);
      if (!data.success) throw new Error(data.message);
      setKnowledge(data.knowledge || []);
      setNotice(data.message || 'Files imported as knowledge.');
    } catch (err) {
      setError(err.message || 'Knowledge upload failed');
    } finally {
      setUploading(false);
    }
  };

  const filtered = knowledge.filter((k) => {
    const q = filter.trim().toLowerCase();
    if (!q) return true;
    return `${k.title} ${k.question} ${k.keywords} ${k.category} ${k.source_name || ''}`
      .toLowerCase()
      .includes(q);
  });

  return (
    <>
      <AuthenticatedNavbar user={user} onLogout={onLogout} />
      <div className="asst-calibrate">
        <button type="button" className="asst-back" onClick={() => navigate(-1)}>
          ← Back
        </button>
        <h1>Calibrate COOL Assistant</h1>
        <p className="asst-lead">
          This is your built-in helper for the COOL Page — not an external embed.
          Tune its name, welcome message, personality, and the knowledge it uses
          to answer guests and members.
        </p>

        {notice && <div className="asst-banner asst-banner--ok">{notice}</div>}
        {error && <div className="asst-banner asst-banner--err">{error}</div>}
        {loading && <p>Loading assistant…</p>}

        {!loading && (
          <>
            <form className="asst-card" onSubmit={saveSettings}>
              <h2>Personality</h2>
              <label className="asst-toggle">
                <input
                  type="checkbox"
                  checked={!!settings.enabled}
                  onChange={(e) =>
                    setSettings((s) => ({ ...s, enabled: e.target.checked ? 1 : 0 }))
                  }
                />
                Show the assistant on the COOL Page
              </label>

              <label>
                Assistant name
                <input
                  value={settings.name}
                  onChange={(e) => setSettings((s) => ({ ...s, name: e.target.value }))}
                  maxLength={80}
                  required
                />
              </label>

              <label>
                Welcome message
                <textarea
                  rows={3}
                  value={settings.welcome_message}
                  onChange={(e) =>
                    setSettings((s) => ({ ...s, welcome_message: e.target.value }))
                  }
                />
              </label>

              <label>
                Personality / instructions (used when an AI key is set)
                <textarea
                  rows={5}
                  value={settings.system_prompt}
                  onChange={(e) =>
                    setSettings((s) => ({ ...s, system_prompt: e.target.value }))
                  }
                />
              </label>

              <label>
                Avatar image URL (leave blank to use the COOL logo)
                <input
                  value={settings.avatar_url}
                  onChange={(e) =>
                    setSettings((s) => ({ ...s, avatar_url: e.target.value }))
                  }
                  placeholder="https://…"
                />
              </label>
              <div className="asst-upload-row">
                {settings.avatar_url ? (
                  <img
                    className="asst-avatar-preview"
                    src={settings.avatar_url}
                    alt="Assistant avatar preview"
                  />
                ) : null}
                <input
                  ref={avatarInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/gif,image/webp"
                  hidden
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.target.value = '';
                    uploadAvatar(file);
                  }}
                />
                <button
                  type="button"
                  className="asst-secondary"
                  disabled={uploading}
                  onClick={() => avatarInputRef.current?.click()}
                >
                  {uploading ? 'Uploading…' : 'Upload avatar'}
                </button>
              </div>

              <label>
                Suggested questions (one per line)
                <textarea
                  rows={4}
                  value={suggestedText}
                  onChange={(e) => setSuggestedText(e.target.value)}
                />
              </label>

              <h3>AI API — answers from the COOL Page</h3>
              <p className="asst-hint">
                The assistant uses this API to read live posts, events, news,
                groups, and jobs on the page, then answer the visitor’s question
                from that content. Choose a provider, pick a GPT model, and save
                your API key. Without a key it falls back to built-in knowledge.
              </p>
              <div className="asst-row">
                <label>
                  Provider
                  <select
                    value={settings.llm_provider || 'none'}
                    onChange={(e) => {
                      const provider = e.target.value;
                      setSettings((s) => {
                        const options = LLM_MODELS[provider] || [];
                        const keep =
                          options.some((m) => m.value === s.llm_model) ? s.llm_model : '';
                        return {
                          ...s,
                          llm_provider: provider,
                          llm_model: provider === 'none' ? '' : keep || DEFAULT_LLM_MODEL[provider] || '',
                        };
                      });
                    }}
                  >
                    <option value="none">Built-in knowledge only</option>
                    <option value="openai">OpenAI</option>
                    <option value="groq">Groq</option>
                    <option value="gemini">Google Gemini</option>
                  </select>
                </label>
                {(settings.llm_provider || 'none') !== 'none' && (
                  <label>
                    {(settings.llm_provider || '') === 'openai' ? 'GPT model' : 'Model'}
                    <select
                      value={settings.llm_model || DEFAULT_LLM_MODEL[settings.llm_provider] || ''}
                      onChange={(e) =>
                        setSettings((s) => ({ ...s, llm_model: e.target.value }))
                      }
                    >
                      {(LLM_MODELS[settings.llm_provider] || []).map((m) => (
                        <option key={m.value} value={m.value}>
                          {m.label}
                        </option>
                      ))}
                      {settings.llm_model &&
                        !(LLM_MODELS[settings.llm_provider] || []).some(
                          (m) => m.value === settings.llm_model
                        ) && (
                          <option value={settings.llm_model}>{settings.llm_model}</option>
                        )}
                    </select>
                  </label>
                )}
              </div>
              <label>
                API key {settings.llm_api_key_set ? '(saved — paste a new one to replace)' : ''}
                <input
                  type="password"
                  autoComplete="off"
                  value={apiKeyDraft}
                  onChange={(e) => setApiKeyDraft(e.target.value)}
                  placeholder={settings.llm_api_key_masked || 'Not set'}
                />
              </label>

              <div className="asst-actions">
                <button type="submit" disabled={saving}>
                  {saving ? 'Saving…' : 'Save personality'}
                </button>
                <button type="button" className="asst-secondary" onClick={restoreDefaults} disabled={saving}>
                  Restore default COOL knowledge
                </button>
              </div>
            </form>

            <div className="asst-card">
              <div className="asst-card-head">
                <h2>Knowledge</h2>
                <button
                  type="button"
                  onClick={() =>
                    setEditing({
                      ...EMPTY_ITEM,
                      sort_order: knowledge.length + 1,
                    })
                  }
                >
                  + Add Q&A
                </button>
              </div>
              <p className="asst-hint">
                Teach the assistant anything about COOL. Type Q&A below, or upload
                TXT, MD, PDF, DOCX, CSV, JSON, or HTML files — the text becomes
                searchable knowledge.
              </p>
              <div
                className={`asst-dropzone${dragOver ? ' asst-dropzone--over' : ''}`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOver(false);
                  uploadKnowledgeFiles(e.dataTransfer.files);
                }}
              >
                <input
                  ref={knowledgeInputRef}
                  type="file"
                  multiple
                  accept=".txt,.md,.markdown,.pdf,.docx,.csv,.json,.html,.htm,.log,text/plain,application/pdf"
                  hidden
                  onChange={(e) => {
                    const list = e.target.files;
                    e.target.value = '';
                    uploadKnowledgeFiles(list);
                  }}
                />
                <p>Drop files here, or</p>
                <button
                  type="button"
                  disabled={uploading}
                  onClick={() => knowledgeInputRef.current?.click()}
                >
                  {uploading ? 'Uploading…' : 'Upload files as knowledge'}
                </button>
                <span>TXT, MD, PDF, DOCX, CSV, JSON, HTML · up to 12 MB each</span>
              </div>
              <input
                className="asst-filter"
                placeholder="Filter knowledge…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              />

              {editing && (
                <form className="asst-editor" onSubmit={saveKnowledge}>
                  <h3>{editing.id ? 'Edit knowledge' : 'New knowledge'}</h3>
                  <label>
                    Title
                    <input
                      value={editing.title}
                      onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                      required
                    />
                  </label>
                  <label>
                    Related questions
                    <input
                      value={editing.question}
                      onChange={(e) =>
                        setEditing({ ...editing, question: e.target.value })
                      }
                    />
                  </label>
                  <label>
                    Answer
                    <textarea
                      rows={6}
                      value={editing.answer}
                      onChange={(e) => setEditing({ ...editing, answer: e.target.value })}
                      required
                    />
                  </label>
                  <div className="asst-row">
                    <label>
                      Keywords
                      <input
                        value={editing.keywords}
                        onChange={(e) =>
                          setEditing({ ...editing, keywords: e.target.value })
                        }
                      />
                    </label>
                    <label>
                      Category
                      <input
                        value={editing.category}
                        onChange={(e) =>
                          setEditing({ ...editing, category: e.target.value })
                        }
                      />
                    </label>
                  </div>
                  <label className="asst-toggle">
                    <input
                      type="checkbox"
                      checked={!!editing.is_active}
                      onChange={(e) =>
                        setEditing({ ...editing, is_active: e.target.checked ? 1 : 0 })
                      }
                    />
                    Active
                  </label>
                  <div className="asst-actions">
                    <button type="submit" disabled={saving}>
                      Save knowledge
                    </button>
                    <button type="button" className="asst-secondary" onClick={() => setEditing(null)}>
                      Cancel
                    </button>
                  </div>
                </form>
              )}

              <ul className="asst-knowledge-list">
                {filtered.map((item) => (
                  <li key={item.id}>
                    <div>
                      <strong>{item.title}</strong>
                      <span>
                        {item.category}
                        {item.source_name ? ` · file: ${item.source_name}` : ''}
                        {!item.is_active ? ' · off' : ''}
                      </span>
                      <p>{item.answer}</p>
                    </div>
                    <div className="asst-item-actions">
                      <button type="button" onClick={() => setEditing({ ...item })}>
                        Edit
                      </button>
                      <button
                        type="button"
                        className="asst-danger"
                        onClick={() => deleteKnowledge(item)}
                      >
                        Delete
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}
      </div>
    </>
  );
}
